import React, { useContext } from "react";
import classNames from "classnames";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AccountingReportContext } from "../AccountingReport.context";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import { getDrillDownClassParams } from "../useGLDrillDownUrl";
import { buildGlDrillDownParams, GlDrillDownParams } from "../glDrillDown.lib";
import { getCashFlowStatementCellClassName } from "./cashFlowStatementCellClassName";
import styles from "./CashFlowStatementTable.module.scss";

interface Props {
    value: number;
    accountCodes: string[];
    period: ReportPeriodWithLabel;
    isTotal: boolean;
    isSummary: boolean;
    isGroupEnd?: boolean;
    onDrillDown?: (params: GlDrillDownParams) => void;
}

/**
 * ViewTable-compatible Cash Flow Statement value cell.
 * Renders as a <div> (not <td>) for CSS-grid row compatibility.
 */
export const CashFlowStatementViewValueCell: React.FC<Props> = React.memo(
    ({
        value,
        accountCodes,
        period,
        isTotal,
        isSummary,
        isGroupEnd,
        onDrillDown,
    }) => {
        const { filters } = useContext(AccountingReportContext);
        const isDifferenceColumn = !!period.comparisonDiff;
        const hasDrillDown = accountCodes.length > 0;
        const { text: displayValue, negativeClassName } =
            useReportAmountDisplay(value, {
                comparisonDiff: period.comparisonDiff,
            });

        const drillDownParams = buildGlDrillDownParams({
            startDate: period.startDate,
            endDate: period.endDate,
            entityId: period.entityId ?? filters.entityId,
            ledgerId: filters.ledgerId,
            accountCodes,
            ...getDrillDownClassParams(period, filters),
            counterpartiesIds: filters.counterpartiesIds,
            withEmptyOrUnverifiedCounterparty:
                filters.withEmptyOrUnverifiedCounterparty,
            hasCounterparty: filters.hasCounterparty,
            accountIds: filters.accountIds,
            subaccountIds: filters.subaccountIds,
        });

        const shouldRenderButton =
            hasDrillDown && !isDifferenceColumn && onDrillDown !== undefined;

        return (
            <ViewTableCell
                className={classNames(
                    styles.numericCell,
                    negativeClassName,
                    getCashFlowStatementCellClassName({ isTotal, isSummary }),
                    {
                        [styles.groupEndCell]: isGroupEnd,
                    },
                )}
            >
                {shouldRenderButton && onDrillDown ? (
                    <button
                        type="button"
                        className={classNames(
                            "data-table__drill-down",
                            styles.drillDownButton,
                        )}
                        onClick={() => onDrillDown(drillDownParams)}
                    >
                        {displayValue}
                    </button>
                ) : (
                    displayValue
                )}
            </ViewTableCell>
        );
    },
);
